import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Lightning,
  Timer,
  Heartbeat,
  Clock,
  ListChecks,
  XCircle,
  ArrowsClockwise,
  Gauge,
  HandPalm,
  BookOpen,
  SmileyNervous,
  Gear,
  Notebook,
  Crosshair,
  Fire,
  Prohibit,
  Path
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const OvertradingExplained = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Overtrading Explained | Why Traders Overtrade and How to Stop',
      description: 'Learn what overtrading is, why traders fall into this habit, and how to stop it using psychology and structured trading methods.',
      path: '/overtrading-explained',
      lang: 'en',
      alternates: getArticleAlternates('/overtrading-explained'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "What is overtrading?",
      answer: "Overtrading is taking more trades than your strategy and market conditions justify. It includes: trading without clear setups, entering positions out of boredom or emotion, trading to recover losses (revenge trading), and ignoring your rules to 'stay active.' The key characteristic is trading for reasons other than genuine edge—usually psychological or emotional drivers rather than strategic ones."
    },
    {
      question: "Why do traders overtrade?",
      answer: "The most common drivers are: boredom (needing action), FOMO (fear of missing moves), revenge trading (trying to recover losses quickly), overconfidence after wins (feeling invincible), and the illusion that more activity means more results. At its core, overtrading is usually an emotional response to market uncertainty—a way to feel in control when the market doesn't cooperate."
    },
    {
      question: "How can I stop overtrading?",
      answer: "Start with structure: define exactly what qualifies as a valid setup before each session. Set a maximum number of trades per day and stick to it. Pre-plan your trades before market open. Step away after losses rather than trying to recover immediately. Track every trade and review patterns in your behavior. Most importantly, accept that some days have no good setups—and that's okay."
    },
    {
      question: "Is overtrading always bad?",
      answer: "Context matters. If you're deliberately testing a high-frequency strategy with proper risk management and analysis, high trade volume might be intentional. But for most retail traders, overtrading typically refers to impulsive, emotional trading beyond your strategy's parameters. The question to ask: 'Am I trading because there's a genuine setup, or because I want to trade?' If it's the latter, it's probably overtrading."
    },
    {
      question: "How do I know if I'm overtrading?",
      answer: "Common signs include: entering trades you can't clearly explain, feeling regret immediately after entering, trading more after losses to 'get it back,' checking charts constantly for any reason to trade, and feeling exhausted or emotionally drained after trading sessions. If your trade frequency increases during losing streaks or when you're bored, that's a strong indicator of overtrading."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Brain size={16} weight="fill" />
            <span>Trading Psychology</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Overtrading Explained
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              Why Traders Do It (And How to Stop)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            Many traders lose money not because their strategy is bad, but because they 
            <strong className="text-white"> trade too much</strong>. They take trades they shouldn't 
            take. They enter when they should wait. They force action when the market gives them 
            nothing. This pattern has a name: <strong className="text-white">overtrading</strong>.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            <strong className="text-white">Overtrading</strong> is one of the most common—and 
            most destructive—habits in trading. It's also one of the hardest to recognize in 
            yourself. The trader who overtrades doesn't feel like they're making a mistake. 
            They feel busy. They feel engaged. They feel like they're working.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            But activity isn't progress. And in trading, more is often less. Understanding 
            <strong className="text-white"> why traders overtrade</strong> and how to break the 
            pattern is essential to long-term survival in the markets.
          </p>
        </section>

        {/* Section 1: What Is Overtrading */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            What Is Overtrading?
          </h2>
          
          <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Overtrading</strong> is taking more trades than 
              your strategy and market conditions justify. It's not about a specific number—what 
              counts as "too many" depends on your approach. But the pattern is recognizable:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                behavior: "Taking Too Many Trades",
                description: "Trading just to trade. Entering positions because you feel like you should be doing something, not because the setup is there.",
                icon: Lightning
              },
              {
                behavior: "Trading Without Clear Setups",
                description: "You can't explain why you entered. The trade 'felt' right, but there was no defined criteria. You're pattern-matching on noise.",
                icon: Target
              },
              {
                behavior: "Forcing Trades Out of Boredom",
                description: "The market is quiet. You're watching. Nothing qualifies. But you enter anyway because waiting feels like wasting time.",
                icon: Clock
              },
              {
                behavior: "Emotional Trading",
                description: "Trading to feel something—excitement after boredom, revenge after losses, validation after wins. The trade serves psychology, not strategy.",
                icon: Heartbeat
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-white/10 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.behavior}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              The common thread: <strong className="text-white">trading for reasons other than 
              genuine edge</strong>. Every trade should have a clear justification based on your 
              strategy. If you can't articulate it, you're probably overtrading.
            </p>
          </div>
        </section>

        {/* Section 2: Why Traders Overtrade */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">02</span>
            Why Traders Overtrade
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Nobody overtrades on purpose. It emerges from psychological pressures that feel 
              completely legitimate in the moment. Recognizing these drivers is the first step 
              to defeating them.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                driver: "Boredom",
                description: "Markets are quiet. You've been watching for hours. The urge to do something—anything—becomes overwhelming. Boredom is surprisingly dangerous.",
                icon: Clock,
                color: "blue"
              },
              {
                driver: "FOMO",
                description: "Price is moving. You didn't have a position. Every tick feels like money you're losing. Fear of missing out pushes you into trades you didn't plan.",
                icon: Fire,
                color: "orange"
              },
              {
                driver: "Revenge Trading",
                description: "You just lost. The money feels stolen. You need to get it back—now. This urgency leads to aggressive, poorly planned trades that usually make things worse.",
                icon: Lightning,
                color: "red"
              },
              {
                driver: "Recovery Pressure",
                description: "You're down for the day/week/month. Each session feels like it must be the turnaround. This pressure creates desperation that manifests as overtrading.",
                icon: TrendUp,
                color: "yellow"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`bg-[#121212] border rounded-lg p-5 ${
                  item.color === 'blue' ? 'border-blue-500/20' :
                  item.color === 'orange' ? 'border-orange-500/20' :
                  item.color === 'red' ? 'border-red-500/20' :
                  'border-yellow-500/20'
                }`}
              >
                <item.icon size={24} weight="duotone" className={`mb-3 ${
                  item.color === 'blue' ? 'text-blue-400' :
                  item.color === 'orange' ? 'text-orange-400' :
                  item.color === 'red' ? 'text-red-400' :
                  'text-yellow-400'
                }`} />
                <h4 className="font-semibold mb-2">{item.driver}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Notice that none of these reasons have anything to do with edge or strategy. 
              They're all <strong className="text-white">emotional responses</strong> to uncertainty, 
              discomfort, or previous outcomes. This is why understanding trading psychology is 
              essential for understanding <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline">why traders fail to improve</Link>.
            </p>
          </div>
        </section>

        {/* Section 3: The Psychological Drivers */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-purple-400 text-lg font-mono">03</span>
            The Psychological Drivers of Overtrading
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              At a deeper level, overtrading reflects how our brains are wired. Trading activates 
              ancient reward circuits that weren't designed for financial markets. Understanding 
              this helps explain why overtrading feels so natural—and why it's so hard to stop.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-purple-500/20 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-purple-400">
              <Brain size={20} weight="duotone" />
              Core Psychological Mechanisms
            </h3>
            <div className="space-y-4">
              {[
                {
                  mechanism: "Impulsive Decision-Making",
                  explanation: "The brain's reward system responds to action, not waiting. Clicking 'buy' triggers dopamine. Sitting on hands triggers nothing. We're biased toward action."
                },
                {
                  mechanism: "Emotional Reactions",
                  explanation: "Losses trigger threat responses. Wins trigger reward responses. Both create momentum—losers want to recover, winners want more. Neither is rational trading."
                },
                {
                  mechanism: "Lack of Discipline",
                  explanation: "Discipline isn't a personality trait; it's a mental resource that depletes. The longer you watch charts without rules, the more likely you are to break."
                }
              ].map((item, index) => (
                <div key={index} className="border-l-2 border-purple-400/50 pl-4">
                  <h4 className="font-medium text-sm mb-1">{item.mechanism}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <BookOpen size={20} weight="duotone" className="text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1 text-sm">"Trading in the Zone"</h4>
                  <p className="text-white/60 text-xs leading-relaxed">
                    Mark Douglas emphasizes that consistent trading requires a <strong className="text-white/80">probability mindset</strong>—accepting 
                    uncertainty and trading only when your edge is present. Overtraders lack this 
                    acceptance; they trade to feel certain, not because certainty exists.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <BookOpen size={20} weight="duotone" className="text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1 text-sm">"Thinking, Fast and Slow"</h4>
                  <p className="text-white/60 text-xs leading-relaxed">
                    Daniel Kahneman's <strong className="text-white/80">System 1 vs System 2</strong> framework 
                    explains overtrading perfectly. System 1 (fast, emotional) screams "trade now!" 
                    System 2 (slow, logical) should filter these impulses—but often doesn't.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Overtrading is System 1 winning.</strong> Every 
              impulsive trade is a moment where emotional, fast thinking overrode logical, slow 
              thinking. The solution isn't to eliminate System 1—that's impossible. It's to 
              build systems that force System 2 to engage before you click.
            </p>
          </div>
        </section>

        {/* Section 4: The Illusion of Productivity */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">04</span>
            The Illusion of Productivity
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Taking more trades feels like working harder.</strong> You're 
              analyzing, entering, managing, exiting. You're engaged. You're busy. But busy isn't 
              the same as profitable.
            </p>
            <p className="text-white/70 leading-relaxed">
              This is the productivity trap: we conflate activity with progress. In most jobs, 
              more effort produces more output. In trading, more trades often produce worse 
              results.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-orange-400 flex items-center gap-2">
                  <Gauge size={18} />
                  The Busy Trader
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• 50 trades per week</li>
                  <li>• Always in a position</li>
                  <li>• Feels productive and engaged</li>
                  <li>• Constantly stressed</li>
                  <li>• Account: flat or declining</li>
                </ul>
              </div>
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-[#00E676] flex items-center gap-2">
                  <Target size={18} />
                  The Selective Trader
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• 8 trades per week</li>
                  <li>• Often on the sidelines</li>
                  <li>• Feels patient, sometimes bored</li>
                  <li>• Lower stress levels</li>
                  <li>• Account: steadily growing</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              The market doesn't reward effort. It rewards edge executed with discipline. 
              Sometimes the best trade is no trade at all.
            </p>
          </div>
        </section>

        {/* Section 5: How Overtrading Destroys Performance */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">05</span>
            How Overtrading Destroys Performance
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Overtrading isn't just suboptimal—it's actively destructive. It attacks your 
              performance from multiple angles simultaneously:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                effect: "Lower Quality Setups",
                description: "When you trade more, you necessarily lower your standards. The 20th trade of the day can't be as good as waiting for the one perfect setup.",
                icon: TrendUp
              },
              {
                effect: "Worse Risk Management",
                description: "Overtrading often comes with oversizing—each trade feels more important than it should be. Position sizes creep up. Stops get ignored.",
                icon: Warning
              },
              {
                effect: "Emotional Fatigue",
                description: "Every trade consumes mental energy. By trade 15, you're exhausted. Decision quality degrades. You make mistakes you wouldn't make fresh.",
                icon: SmileyNervous
              },
              {
                effect: "Inconsistent Results",
                description: "Some days you take 5 trades, some 25. This variance makes it impossible to evaluate your actual edge. Noise drowns out signal.",
                icon: ChartLineUp
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.effect}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This is a common <strong className="text-white">trading psychology mistake</strong>: 
              treating trading like a job where showing up and working hard guarantees results. 
              The market doesn't care how hard you work.
            </p>
          </div>
        </section>

        {/* Section 6: Signs You Are Overtrading */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-yellow-400 text-lg font-mono">06</span>
            Signs You Are Overtrading
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Overtrading is easier to recognize in others than yourself. But these warning signs 
              can help you catch the pattern before it does too much damage:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-yellow-500/20 rounded-lg p-6 mb-8">
            <h3 className="font-semibold mb-4 text-yellow-400 flex items-center gap-2">
              <Warning size={20} weight="duotone" />
              Warning Signs Checklist
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Trading without a written plan",
                "Entering positions impulsively",
                "Feeling regret immediately after entry",
                "Increasing trades after losses",
                "Unable to explain why you entered",
                "Checking charts constantly for 'any' reason to trade",
                "Trading different from your backtested strategy",
                "Feeling exhausted after trading sessions",
                "Saying 'I shouldn't have taken that' often",
                "Trade frequency varies wildly day to day"
              ].map((sign, index) => (
                <div key={index} className="flex items-start gap-2">
                  <XCircle size={16} weight="fill" className="text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{sign}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              If you recognize three or more of these patterns, you're likely overtrading. 
              The good news: awareness is the first step. Most traders don't even realize 
              they have this problem.
            </p>
          </div>
        </section>

        {/* Section 7: How to Stop Overtrading */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            How to Stop Overtrading (Practical Steps)
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Stopping overtrading isn't about willpower—it's about building systems that make 
              overtrading difficult. Here are concrete steps that work:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                step: "1",
                action: "Define Clear Setups",
                description: "Write down exactly what qualifies as a valid trade. Be specific: timeframe, confirmation, context. If it doesn't match, you don't trade.",
                icon: ListChecks
              },
              {
                step: "2",
                action: "Limit Trades Per Day",
                description: "Set a maximum—maybe 3-5 trades. When you hit the limit, you're done. This forces selectivity and prevents spiral trading.",
                icon: Prohibit
              },
              {
                step: "3",
                action: "Pre-Plan Your Trades",
                description: "Before the market opens, identify potential setups. If the setup doesn't appear, you don't force it. Plan the trade, trade the plan.",
                icon: Notebook
              },
              {
                step: "4",
                action: "Create Entry Rules",
                description: "Require a checklist before every entry. 'Does this match my criteria? Have I written down my reason?' This engages System 2 before System 1 acts.",
                icon: Crosshair
              },
              {
                step: "5",
                action: "Step Away After Losses",
                description: "Implement a mandatory break after 2-3 consecutive losses. 30 minutes minimum. This prevents revenge trading and lets emotions cool.",
                icon: HandPalm
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00E676] font-bold">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <item.icon size={16} className="text-[#00E676]" />
                    {item.action}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              These aren't optional suggestions—they're structural interventions that make 
              overtrading physically harder. Willpower fails. Systems persist.
            </p>
          </div>
        </section>

        {/* Section 8: The Role of Structure and Data */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            The Role of Structure and Data
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Structure and data are the antidotes to impulsive behavior. When you track 
              everything and follow rules, overtrading becomes visible—and therefore manageable.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                benefit: "Tracking Reduces Impulse",
                description: "Knowing you have to log every trade creates friction. That friction is enough to make you pause and question impulsive decisions.",
                icon: Notebook
              },
              {
                benefit: "Review Builds Awareness",
                description: "Looking at your trade log reveals patterns. 'I took 15 trades on Tuesday, 4 on Wednesday.' Now you can ask why—and change it.",
                icon: Eye
              },
              {
                benefit: "Data Creates Discipline",
                description: "When you see that your win rate drops after trade 5 each day, you have evidence for your limits. Data turns rules into facts.",
                icon: ChartLineUp
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2 text-sm">{item.benefit}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Many traders use tools like <strong className="text-white">GENI</strong> to track 
              their trades and identify patterns of overtrading, making it easier to build 
              discipline over time. Learning <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> is 
              the foundation of this self-awareness.
            </p>
          </div>
        </section>

        {/* Section 9: From Emotional to Structured Trading */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            From Emotional Trading to Structured Trading
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              The transition from overtrading to disciplined trading isn't instant. It's a 
              gradual shift in mindset and behavior. Here's what the journey looks like:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="p-4 bg-white/5 border-b border-white/10">
              <h4 className="font-semibold text-sm text-white/60">The Transformation</h4>
            </div>
            <div className="p-6 space-y-4">
              {[
                { from: "Impulsive", to: "Intentional", desc: "Trades have clear reasons, not just feelings" },
                { from: "Reactive", to: "Planned", desc: "Setups are identified before they happen" },
                { from: "Random", to: "Data-Driven", desc: "Decisions are based on evidence, not hope" },
                { from: "Emotional", to: "Systematic", desc: "Rules override temporary feelings" },
                { from: "Quantity-Focused", to: "Quality-Focused", desc: "Fewer trades, better trades" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="text-red-400 text-sm font-medium w-24">{item.from}</span>
                  <ArrowRight size={16} className="text-white/30" />
                  <span className="text-[#00E676] text-sm font-medium w-24">{item.to}</span>
                  <span className="text-white/50 text-xs flex-1">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This transformation is what <Link to="/how-to-improve-day-trading" className="text-[#00E676] hover:underline">improving in day trading</Link> actually 
              looks like. It's not finding a better indicator—it's becoming a better decision-maker.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Reducing <strong className="text-white">overtrading</strong> starts with awareness, 
              structure, and better decision-making processes. It's not about trading less because 
              less is inherently better—it's about trading only when your edge is present.
            </p>
            <p className="text-white/70 leading-relaxed">
              The market rewards patience more than activity. The traders who succeed long-term 
              are often the ones who learned to do nothing when nothing should be done. That 
              discipline is harder than it sounds—but it's learnable.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
              <Link to="/how-to-improve-day-trading" className="text-[#00E676] hover:underline text-sm">
                How to Improve in Day Trading →
              </Link>
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
              <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline text-sm">
                Improve Risk Reward Ratio →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck.</p>
            </Link>
            <Link to="/how-to-improve-day-trading" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Improve in Day Trading</h3>
              <p className="text-white/50 text-sm">What actually works for getting better.</p>
            </Link>
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to trade review.</p>
            </Link>
            <Link to="/win-rate-trading" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Win Rate Explained</h3>
              <p className="text-white/50 text-sm">Why win rate alone can mislead you.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Brain size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Build Better Trading Habits
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Track your trades, identify patterns, and build the discipline that separates 
              consistent traders from struggling ones.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Overtrading Explained: Why Traders Do It (And How to Stop)",
              "description": "Learn what overtrading is, why traders fall into this habit, and how to stop it using psychology and structured trading methods.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/overtrading-explained"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OvertradingExplained;
